/**
 * 大ステップアコーディオンの開閉状態を、画面を離れて戻る間だけ覚えておく。
 *
 * ワークフロー詳細から取引契約管理などへ移動し「←」で戻ると、開いていた大ステップが
 * 閉じた状態に戻る (2026-09-02 ドッグフーディング報告 #7067)。開閉は
 * `BigStepAccordion` の `useState` に閉じており、route 遷移で page ごと unmount
 * されるため初期値へリセットされるのが原因。
 *
 * ## なぜ sessionStorage か
 *
 * 同じ「開閉の意思を覚える」用途である副パネルの
 * `components/layouts/MainLayout/secondaryMenuAutoOpen.ts` に揃える。あちらの
 * 判断理由がそのまま当てはまる:
 *
 * > localStorage だと「昔一度閉じた」が永続して、いつまでも開かない状態になる。
 *
 * 大ステップも同じで、既定は「先頭の大ステップを開く」「一覧の要承認・要対応から
 * 遷移した時はその大ステップを開く」であり、**次に開いた時はこの既定に戻ってほしい**。
 * localStorage にすると数日前の開閉が既定を上書きし続ける。タブを閉じたら忘れる
 * sessionStorage が用途に合う。
 *
 * フィルタ保持 (`~/hooks/usePersistedFilters`) が localStorage なのは
 * spec AC-SC-031 が「再訪時に復元」まで求めているためで、こちらにはその要求が無い。
 *
 * ## ケース単位で持つ
 *
 * 別のワークフローケースを開いた時に前のケースの開閉が効くと、ステップ構成が違う
 * ぶん意図しない開き方になる。key にケース ID を含めて分離する。
 */

/** ケースごとの key。ケースをまたいで開閉が混ざらないようにする。 */
const storageKey = (caseId: string): string =>
  `ebalance:workflow-case-detail:open-big-steps:${caseId}`

/**
 * 開いている大ステップの `displayOrder` 集合を読む。
 *
 * 未保存・壊れた値・Web Storage が使えない環境 (Safari private mode 等) では
 * `undefined` を返し、呼び出し側の既定 (先頭 / focus 対象) に委ねる
 * (`libs/localStorage.ts` と同じ「例外は握りつぶして未設定扱い」の慣習)。
 */
export function readOpenBigSteps(caseId: string): Set<number> | undefined {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(storageKey(caseId))
  } catch {
    return undefined
  }
  if (raw === null) return undefined
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return undefined
    const orders = parsed.filter((v): v is number => typeof v === 'number')
    // 空配列は「すべて閉じた」という**意思**なので undefined と区別して返す。
    return new Set(orders)
  } catch {
    return undefined
  }
}

/** 開いている大ステップの `displayOrder` 集合を保存する。失敗は無視する。 */
export function writeOpenBigSteps(caseId: string, open: Set<number>): void {
  try {
    sessionStorage.setItem(
      storageKey(caseId),
      JSON.stringify([...open].sort((a, b) => a - b)),
    )
  } catch {
    // Web Storage が使えない環境では保持しない (既定の開き方で動作する)。
  }
}

/**
 * この大ステップを初期表示で開くか。
 *
 * 保存された意思があればそれに従い、無ければ既定 (先頭 or focus 対象) に倒す。
 *
 * **focus 対象は保存より優先する。** 一覧の「要承認 / 要対応」から遷移した時は
 * その大ステップで作業する意図が明確で (spec AC-WFW-001 §243 の focus 遷移)、
 * 前に閉じていたからといって畳んだままにすると、遷移してきた目的の行が見えない。
 */
export function shouldOpenBigStep({
  displayOrder,
  isFirst,
  isFocused,
  saved,
}: {
  displayOrder: number
  isFirst: boolean
  isFocused: boolean
  saved: Set<number> | undefined
}): boolean {
  if (isFocused) return true
  if (saved !== undefined) return saved.has(displayOrder)
  return isFirst
}
